import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, VStack, Heading, Text, Box, Badge, HStack,
  OrderedList, ListItem, Divider, Button, Link, SimpleGrid, Icon,
} from "@chakra-ui/react";
import { FiArrowLeft, FiArrowRight, FiZap, FiGlobe, FiMail, FiCheckCircle } from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const LaunchModule: React.FC = () => {
  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.md" py={10}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <VStack spacing={3} align="start">
            <HStack>
              <Badge colorScheme="purple">Launch Module</Badge>
              <Text fontSize="xs" color="gray.400">20 Mar 2026</Text>
            </HStack>
            <Heading size="lg" color="#2c5282">Launch — Create Tenant Websites with AI</Heading>
            <Text color="gray.600">
              Describe a business, let AI design the site, click one button — and watch a fully branded website deploy automatically in under 5 minutes.
            </Text>
          </VStack>

          <Divider />

          {/* What's New */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>What's New</Heading>
            <Text color="gray.600" mb={4}>
              The Launch module automates the entire process of creating a new Business Builder tenant website. What used to take 2-3 hours of manual setup now happens automatically with a progress bar showing each step completing in real-time.
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="purple.200" bg="purple.50">
                <HStack mb={2}>
                  <Icon as={FiZap} color="purple.500" />
                  <Text fontWeight="bold" color="purple.700">AI Site Planning</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Describe a business and our AI designs the perfect setup — site name, tagline, brand colours, which modules to include, everything.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="blue.200" bg="blue.50">
                <HStack mb={2}>
                  <Icon as={FiGlobe} color="blue.500" />
                  <Text fontWeight="bold" color="blue.700">One-Click Deploy</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Click "Launch" and watch 9 automated steps complete: code setup, branding, hosting, email server, domain, and deployment.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="green.200" bg="green.50">
                <HStack mb={2}>
                  <Icon as={FiMail} color="green.500" />
                  <Text fontWeight="bold" color="green.700">Full Infrastructure</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Each site gets its own email server, SMS capability, hosting, and a branded domain at sitename.tommillerservices.com.
                </Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor="teal.200" bg="teal.50">
                <HStack mb={2}>
                  <Icon as={FiCheckCircle} color="teal.500" />
                  <Text fontWeight="bold" color="teal.700">12 Core Modules</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Every site includes: CRM, Opportunities, Proposals, Calendar, Bills, Email, LMS, Rewards, Media CDN, Company Knowledge, and more.
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider />

          {/* How It Works */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>How It Works</Heading>
            <OrderedList spacing={3} pl={4} color="gray.600">
              <ListItem>
                <Text as="span" fontWeight="bold">Go to Launch</Text> — Navigate to the Launch module from the sidebar menu
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Click "Launch New Business"</Text> — Choose Quick Create or AI-Assisted
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Describe the business</Text> — Tell the AI about the business in a few sentences (e.g., "A house painting company in Brisbane called Joe's Painting")
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Review the AI spec</Text> — The AI generates a complete site plan: name, tagline, brand colours, suggested modules. Edit anything you want.
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Click Launch</Text> — Watch the 9-step progress bar as your site is automatically provisioned
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">Site goes live</Text> — Your branded website is live at sitename.tommillerservices.com with all modules ready to use
              </ListItem>
            </OrderedList>
          </Box>

          <Divider />

          {/* The 9 Steps */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>The 9 Automated Steps</Heading>
            <Text color="gray.600" mb={3}>When you click Launch, the system automatically:</Text>
            <OrderedList spacing={2} pl={4} color="gray.600" fontSize="sm">
              <ListItem>Sets up the website code (from our master template)</ListItem>
              <ListItem>Personalises the branding (colours, name, logo)</ListItem>
              <ListItem>Configures hosting</ListItem>
              <ListItem>Sets up the web address (sitename.tommillerservices.com)</ListItem>
              <ListItem>Creates the email server</ListItem>
              <ListItem>Configures email sending</ListItem>
              <ListItem>Saves all configuration</ListItem>
              <ListItem>Deploys the website (builds and publishes)</ListItem>
              <ListItem>Your business has launched!</ListItem>
            </OrderedList>
          </Box>

          <Divider />

          {/* Domain Check */}
          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>Domain Name Check</Heading>
            <Text color="gray.600">
              You can also check if a custom domain name is available (e.g., joespainting.com) right from the Launch form. If it's available, we show you the price. If not, your site still gets a free default URL at sitename.tommillerservices.com.
            </Text>
          </Box>

          <Divider />

          {/* Try It Now */}
          <Box bg="green.50" p={5} borderRadius="md" border="1px solid" borderColor="green.200">
            <Heading size="md" color="#38a169" mb={3}>Try It Now!</Heading>
            <Text mb={3} color="gray.600">Launch your first test site and see how fast it is.</Text>
            <Link as={RouterLink} to="/launch" _hover={{ textDecoration: "none" }}>
              <Button colorScheme="green" size="md" rightIcon={<FiArrowRight />}>
                Go to Launch Module
              </Button>
            </Link>
          </Box>

          {/* Sign off */}
          <Box>
            <Text color="gray.600">As always, reach out if you have any questions or feedback.</Text>
            <Text mt={4} color="gray.600">
              Regards,<br />
              <strong>Dr Thomas Miller</strong><br />
              <Text as="em" color="gray.500">Business Builder</Text>
            </Text>
          </Box>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default LaunchModule;
